import { UserRoles } from '@/data/user/role';
import Feature from '@/domain/core/Feature';
import { Permission } from '@/features/permissions';
import { TUser } from '@/types/user';

export interface IHeaderTab {
	path: string;
	name: string;
	dataTestId: string;
	visible?: boolean;
    activeRegexp?: RegExp;
}

interface IHeaderStore {
	tabsConfig: IHeaderTab[];
}

const expertsPageRegexp = /experts\/?$/;
const expertisesPageRegexp = /experts\/\d+\/expertises/;

class HeaderStore implements IHeaderStore {
	constructor(public user: TUser) {
		this.user = user;
	}

	get defaultHeaderMenu(): IHeaderTab[] {
		return [
			{
				path: '/projects',
				dataTestId: 'projects',
				name: 'projects.l',
				visible: Permission.instance.canAccess('projectsListPage'),
			},
			{
				path: '/tutorial',
				dataTestId: 'tutorial',
				name: 'tutorial.l',
			},
			{
				path: '/experts',
                activeRegexp: expertsPageRegexp,
				dataTestId: 'experts',
				name: 'experts.ls',
				visible: Permission.instance.canAccess('expertsListPage'),
			},
			{
				path: `/experts/${this.user.id}/expertises/new`,
                activeRegexp: expertisesPageRegexp,
				dataTestId: 'experts',
				name: 'evaluation.l',
				visible: Permission.instance.canAccess('expertiseNewListPage'),
			},
			{
				path: '/users',
				dataTestId: 'users',
				name: 'user.ls',
				visible: Permission.instance.canAccess('usersListPage'),
			},
			{
				path: '/drafts',
				dataTestId: 'drafts',
				name: 'drafts.ls',
				visible: Permission.instance.canAccess('draftsPage'),
			},
			{
				path: '/reports',
				dataTestId: 'reports',
				name: 'report.ls',
				visible: Feature.isFeatureEnabled('reportsPage') && Permission.instance.canAccessSome([
					'readTaskStatusReport', 
					'readProjectsActivityReport', 
					'readMonitoringResultsReport', 
					'readStatusSelectionsReport', 
					'readCalendarReport'
				]),
			},
			{
				path: '/partners',
				dataTestId: 'partners',
				name: 'partner.ls',
				visible: Feature.isFeatureEnabled('partners') && Permission.instance.canAccess('partnersListPage'),
			},
			{
				path: '/import-projects',
				dataTestId: 'import-projects',
				name: 'importProjects.l',
				visible: Feature.isFeatureEnabled('importProjects') && Permission.instance.canAccess('importProjectsPage'),
			},
			{
				path: '/admin',
				dataTestId: 'admin',
				name: 'common.settings',
				visible:
					Feature.isFeatureEnabled('adminPage') &&
					Permission.instance.canAccessSome([
						'adminDictionaryPage',
						'adminSelectionPage',
						'adminBusinessProcessPage',
						'adminNotificationTemplatePage',
						'adminRoadmapTemplatePage',
						'adminCollegialPage',
						'adminRolePage',
						'adminProjectTemplatePage',
						'adminCriteriaPage',
						'adminConsentPage',
					]),
			},
		];
	}

	get tabsConfig() {
		if (!this.user.role.shouldWorkAsLegacyRole) {
			return this.defaultHeaderMenu;
		}

		switch (this.user.role?.name) {
			case UserRoles.Expert:
			case UserRoles.ThemeExpert:
				return [
					{
						path: `/experts/${this.user.id}/expertises/new`,
                        activeRegexp: expertisesPageRegexp,
						dataTestId: 'experts',
						name: 'evaluation.l',
					},
					{
						path: '/tutorial',
						dataTestId: 'tutorial',
						name: 'tutorial.l',
					},
				];
			case UserRoles.TopManager:
			case UserRoles.Admin:
				return [
					{
						path: '/projects',
						dataTestId: 'projects',
						name: 'projects.l',
					},
					{
						path: '/tutorial',
						dataTestId: 'tutorial',
						name: 'tutorial.l',
					},
					{
						path: '/import-projects',
						dataTestId: 'import-projects',
						name: 'importProjects.l',
						visible: Feature.isFeatureEnabled('importProjects'),
					},
					{
						path: '/experts',
                        activeRegexp: expertsPageRegexp,
						dataTestId: 'experts',
						name: 'experts.ls',
					},
					{
						path: '/users',
						dataTestId: 'users',
						name: 'user.ls',
					},
					{
						path: '/reports',
						dataTestId: 'reports',
						name: 'report.ls',
						visible: Feature.isFeatureEnabled('reportsPage'),
					},
					{
						path: '/drafts',
						dataTestId: 'drafts',
						name: 'drafts.ls',
					},
					{
						path: '/admin',
						dataTestId: 'admin',
						name: 'common.settings',
					},
				];
			case UserRoles.ThemeAdmin:
				return [
					{
						path: '/projects',
						dataTestId: 'projects',
						name: 'projects.l',
					},
					{
						path: '/tutorial',
						dataTestId: 'tutorial',
						name: 'tutorial.l',
					},
					{
						path: '/import-projects',
						dataTestId: 'import-projects',
						name: 'importProjects.l',
						visible: Feature.isFeatureEnabled('importProjects'),
					},
					{
						path: '/experts',
                        activeRegexp: expertsPageRegexp,
						dataTestId: 'experts',
						name: 'experts.ls',
					},
					{
						path: '/users',
						dataTestId: 'users',
						name: 'user.ls',
					},
					{
						path: '/reports',
						dataTestId: 'reports',
						name: 'report.ls',
						visible: Feature.isFeatureEnabled('reportsPage'),
					},
					{
						path: '/drafts',
						dataTestId: 'drafts',
						name: 'drafts.ls',
					},
				];
			case UserRoles.GlobalAdmin:
				return [
					{
						path: '/projects',
						dataTestId: 'projects',
						name: 'projects.l',
					},
					{
						path: '/tutorial',
						dataTestId: 'tutorial',
						name: 'tutorial.l',
					},
					{
						path: '/import-projects',
						dataTestId: 'import-projects',
						name: 'importProjects.l',
						visible: Feature.isFeatureEnabled('importProjects'),
					},
					{
						path: '/experts',
                        activeRegexp: expertsPageRegexp,
						dataTestId: 'experts',
						name: 'experts.ls',
					},
					{
						path: '/users',
						dataTestId: 'users',
						name: 'user.ls',
					},
					{
						path: '/drafts',
						dataTestId: 'drafts',
						name: 'drafts.ls',
					},
					{
						path: '/reports',
						dataTestId: 'reports',
						name: 'report.ls',
						visible: Feature.isFeatureEnabled('reportsPage'),
					},
					{
						path: '/partners',
						dataTestId: 'partners',
						name: 'partner.ls',
						visible:
							Feature.isFeatureEnabled('partners') && Permission.instance.canAccess('partnersListPage'),
					},
					{
						path: '/admin',
						dataTestId: 'admin',
						name: 'common.settings',
					},
				];
			case UserRoles.Tracker:
				return [
					{
						path: '/projects',
						dataTestId: 'projects',
						name: 'projects.l',
					},
					{
						path: '/tutorial',
						dataTestId: 'tutorial',
						name: 'tutorial.l',
					},
					{
						path: '/experts',
                        activeRegexp: expertsPageRegexp,
						dataTestId: 'experts',
						name: 'experts.ls',
					},
					{
						path: '/drafts',
						dataTestId: 'drafts',
						name: 'drafts.ls',
					},
				];
			case UserRoles.ThemeTracker:
				return [
					{
						path: '/projects',
						dataTestId: 'projects',
						name: 'projects.l',
					},
					{
						path: '/tutorial',
						dataTestId: 'tutorial',
						name: 'tutorial.l',
					},
					{
						path: '/experts',
                        activeRegexp: expertsPageRegexp,
						dataTestId: 'experts',
						name: 'experts.ls',
					},
					{
						path: '/drafts',
						dataTestId: 'drafts',
						name: 'drafts.ls',
					},
				];
			case UserRoles.HeadTracker:
				return [
					{
						path: '/projects',
						dataTestId: 'projects',
						name: 'projects.l',
					},
					{
						path: '/tutorial',
						dataTestId: 'tutorial',
						name: 'tutorial.l',
					},
					{
						path: '/experts',
                        activeRegexp: expertsPageRegexp,
						dataTestId: 'experts',
						name: 'experts.ls',
					},
					{
						path: '/drafts',
						dataTestId: 'drafts',
						name: 'drafts.ls',
					},
				];
			case UserRoles.Leader:
				return [
					{
						path: '/projects',
						dataTestId: 'projects',
						name: 'projects.l',
					},
					{
						path: '/tutorial',
						dataTestId: 'tutorial',
						name: 'tutorial.l',
					},
					{
						path: '/drafts',
						dataTestId: 'drafts',
						name: 'drafts.ls',
					},
				];
			case UserRoles.Manager:
				return [
					{
						path: '/projects',
						dataTestId: 'projects',
						name: 'projects.l',
					},
					{
						path: '/tutorial',
						dataTestId: 'tutorial',
						name: 'tutorial.l',
					},
					{
						path: '/users',
						dataTestId: 'users',
						name: 'user.ls',
					},
					{
						path: '/experts',
                        activeRegexp: expertsPageRegexp,
						dataTestId: 'experts',
						name: 'experts.ls',
					},
				];
			case UserRoles.Employee:
				return [
					{
						path: '/projects',
						dataTestId: 'projects',
						name: 'projects.l',
					},
					{
						path: '/tutorial',
						dataTestId: 'tutorial',
						name: 'tutorial.l',
					},
					{
						path: '/users',
						dataTestId: 'users',
						name: 'user.ls',
					},
					{
						path: '/experts',
                        activeRegexp: expertsPageRegexp,
						dataTestId: 'experts',
						name: 'experts.ls',
					},
				];
			default:
				return this.defaultHeaderMenu;
		}
	}
}

export default HeaderStore;
